import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import * as QRCode from 'qrcode'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Download, Upload, X, Loader2 } from 'lucide-react'

interface QRCodeGeneratorProps {
    profileUrl: string
    businessName: string
    allowBranding?: boolean
}

const SIZES = [
    { id: 'small', name: 'Small', value: 200, description: '2" x 2"' },
    { id: 'medium', name: 'Medium', value: 400, description: '4" x 4"' },
    { id: 'large', name: 'Large', value: 600, description: '6" x 6"' },
    { id: 'extraLarge', name: 'Extra Large', value: 800, description: '8" x 8"' },
]

export function QRCodeGenerator({ profileUrl, businessName, allowBranding = false }: QRCodeGeneratorProps) {
    const { t } = useTranslation('dashboard')
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const [selectedSize, setSelectedSize] = useState('medium')
    const [logo, setLogo] = useState<File | null>(null)
    const [logoPreview, setLogoPreview] = useState<string | null>(null)
    const [isGenerating, setIsGenerating] = useState(false)
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)

    const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                alert('Logo must be less than 2MB')
                return
            }
            setLogo(file)
            const reader = new FileReader()
            reader.onload = (e) => {
                setLogoPreview(e.target?.result as string)
            }
            reader.readAsDataURL(file)
        }
    }

    const removeLogo = () => {
        setLogo(null)
        setLogoPreview(null)
    }

    const generateQRCode = async () => {
        setIsGenerating(true)
        try {
            const sizeValue = SIZES.find((s) => s.id === selectedSize)?.value || 400

            const canvas = canvasRef.current
            if (!canvas) return

            await QRCode.toCanvas(canvas, profileUrl, {
                width: sizeValue,
                margin: 2,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF',
                },
            })

            if (logoPreview) {
                const ctx = canvas.getContext('2d')
                if (ctx) {
                    const logoSize = sizeValue * 0.2
                    const logoX = (sizeValue - logoSize) / 2
                    const logoY = (sizeValue - logoSize) / 2

                    ctx.fillStyle = '#FFFFFF'
                    ctx.beginPath()
                    ctx.arc(sizeValue / 2, sizeValue / 2, logoSize / 2 + 4, 0, Math.PI * 2)
                    ctx.fill()

                    const img = new Image()
                    img.crossOrigin = 'anonymous'
                    img.onload = () => {
                        ctx.save()
                        ctx.beginPath()
                        ctx.arc(sizeValue / 2, sizeValue / 2, logoSize / 2, 0, Math.PI * 2)
                        ctx.clip()
                        ctx.drawImage(img, logoX, logoY, logoSize, logoSize)
                        ctx.restore()
                        setQrDataUrl(canvas.toDataURL('image/png'))
                    }
                    img.src = logoPreview
                }
            } else {
                setQrDataUrl(canvas.toDataURL('image/png'))
            }
        } catch (error) {
            console.error('Failed to generate QR code:', error)
        } finally {
            setIsGenerating(false)
        }
    }

    const downloadQRCode = () => {
        if (!qrDataUrl) return

        const link = document.createElement('a')
        link.download = `${businessName.replace(/\s+/g, '-').toLowerCase()}-qr-code.png`
        link.href = qrDataUrl
        link.click()
    }

    const sizeValue = SIZES.find((s) => s.id === selectedSize)?.value || 400

    return (
        <Card>
            <CardHeader>
                <CardTitle>{t('qrcode.generator.title')}</CardTitle>
                <CardDescription>
                    {t('qrcode.generator.description')}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('qrcode.generator.size')}</label>
                    <div className="grid grid-cols-4 gap-2">
                        {SIZES.map((size) => (
                            <button
                                key={size.id}
                                type="button"
                                onClick={() => setSelectedSize(size.id)}
                                className={`p-2 border rounded-lg text-center transition-colors ${selectedSize === size.id
                                    ? 'border-primary bg-primary/5'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <div className="text-sm font-medium">{t(`qrcode.generator.${size.id}`)}</div>
                                <div className="text-xs text-gray-500">{size.description}</div>
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('qrcode.generator.customLogo')}
                        {!allowBranding && (
                            <span className="ml-2 text-xs text-amber-600">(Premium feature)</span>
                        )}
                    </label>
                    {!allowBranding ? (
                        <div className="flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                            <div className="text-center">
                                <span className="text-sm text-gray-400">
                                    Upgrade to Professional or Unlimited to enable custom logo branding
                                </span>
                            </div>
                        </div>
                    ) : logoPreview ? (
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <img
                                    src={logoPreview}
                                    alt="Logo preview"
                                    className="w-16 h-16 rounded-lg object-contain border"
                                />
                                <button
                                    type="button"
                                    onClick={removeLogo}
                                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                            <p className="text-sm text-gray-500">
                                {logo?.name} ({(logo?.size || 0) / 1024}KB)
                            </p>
                        </div>
                    ) : (
                        <label className="flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400">
                            <div className="text-center">
                                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                <span className="text-sm text-gray-500">{t('qrcode.generator.uploadLogo')}</span>
                                <p className="text-xs text-gray-400 mt-1">{t('qrcode.generator.logoConstraints')}</p>
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleLogoUpload}
                            />
                        </label>
                    )}
                </div>

                <Button onClick={generateQRCode} disabled={isGenerating} className="w-full">
                    {isGenerating ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('qrcode.generator.generating')}
                        </>
                    ) : (
                        t('qrcode.generator.generate')
                    )}
                </Button>

                {qrDataUrl && (
                    <div className="space-y-4">
                        <div className="flex justify-center p-4 bg-gray-50 rounded-lg">
                            <img
                                src={qrDataUrl}
                                alt="Generated QR Code"
                                className="max-w-full"
                                style={{ width: Math.min(sizeValue, 300), height: 'auto' }}
                            />
                        </div>
                        <Button onClick={downloadQRCode} className="w-full">
                            <Download className="w-4 h-4 mr-2" />
                            {t('qrcode.generator.download')}
                        </Button>
                    </div>
                )}

                <canvas ref={canvasRef} style={{ display: 'none' }} />
            </CardContent>
        </Card>
    )
}
